import { makeStyles, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { BRANCHEN_KRITISCH } from "./enums";
import { ziffernFilter } from "./GrossZahlEingabe";

const useStyles = makeStyles({
  list: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  card: {
    backgroundColor: BRAND.white,
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
  },
  head: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    cursor: "pointer",
  },
  checkbox: {
    accentColor: BRAND.purple,
    cursor: "pointer",
    width: "18px",
    height: "18px",
  },
  label: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
    flexGrow: 1,
  },
  detail: {
    marginTop: "10px",
    paddingTop: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    paddingBottom: "10px",
    backgroundColor: "#FFF8E8",
    ...shorthands.borderLeft("3px", "solid", BRAND.gold),
    ...shorthands.borderRadius("4px"),
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "10px",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "3fr 1fr",
    },
  },
  field: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  fieldLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("4px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    transitionProperty: "border-color",
    transitionDuration: "120ms",
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
    },
  },
});

export type BranchenSelections = Record<
  string,
  { selected?: boolean; beschreibung?: string; flaeche?: string }
>;

type Props = {
  value: BranchenSelections;
  onChange: (next: BranchenSelections) => void;
};

const BranchenChecklist = ({ value, onChange }: Props) => {
  const styles = useStyles();
  const setEntry = (
    branche: string,
    patch: Partial<{ selected: boolean; beschreibung: string; flaeche: string }>
  ) => {
    onChange({
      ...value,
      [branche]: { ...(value[branche] ?? {}), ...patch },
    });
  };

  return (
    <div className={styles.list}>
      {BRANCHEN_KRITISCH.map((b) => {
        const entry = value[b] ?? {};
        const checked = !!entry.selected;
        return (
          <div key={b} className={styles.card}>
            <label className={styles.head}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={checked}
                onChange={(e) => setEntry(b, { selected: e.target.checked })}
              />
              <span className={styles.label}>{b}</span>
            </label>
            {checked && (
              <div className={styles.detail}>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Genaue Beschreibung</span>
                  <input
                    className={styles.input}
                    value={entry.beschreibung ?? ""}
                    onChange={(e) => setEntry(b, { beschreibung: e.target.value })}
                  />
                </div>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Fläche (m²)</span>
                  <input
                    className={styles.input}
                    value={entry.flaeche ?? ""}
                    onChange={(e) => setEntry(b, { flaeche: ziffernFilter(e.target.value, { dezimal: true }) })}
                    inputMode="numeric"
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BranchenChecklist;
